import React from 'react';
import { TestState } from '@/store/InstallationValidation/constants';
import { Grid, theme, Tooltip } from 'antd';
import CommentsModal from '../CommentsModal/CommentsModal';
import { observer } from 'mobx-react';
import { useStores } from '@/hooks/use-stores';
import MobileCommentDrawer from '../MobileCommentDrawer/MobileCommentDrawer';
import { useTranslation } from 'react-i18next';
import MessageOutlinedIcon from '@/comp/common/icons/MessageOutlinedIcon';
import LightningSlashIcon from '@/comp/common/icons/LightningSlashIcon';
import {
  checkHasComments,
  getClassNameByTestStateAndStatus,
  getCommentButtonText,
  getStatusIcon,
  getStatusText,
  hasError,
  shouldShowCommentButton,
  TestStatusCardProps,
} from './TestStatusCard.utils';
import { CardWrapper, Content, Footer, TestStatus } from './TestStatusCard.styles';
import ConnectivityLoadIcon from '@/comp/common/icons/ConnectivityLoadIcon';
import ConnectivityUnloadIcon from '@/comp/common/icons/ConnectivityUnloadIcon';

const { useBreakpoint } = Grid;
const { useToken } = theme;

const TestStatusCard: React.FC<TestStatusCardProps> = observer(
  ({ title, subTitle, LOAD, UNLOAD, testState, isNoPowerSensorVisible = false }) => {
    const { installationValidationStore } = useStores();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { t } = useTranslation();
    const { token } = useToken();

    const loadComment = installationValidationStore.getComment(LOAD.id)
      ? installationValidationStore.getComment(LOAD.id)
      : LOAD.comment;
    const unloadComment = installationValidationStore.getComment(UNLOAD.id)
      ? installationValidationStore.getComment(UNLOAD.id)
      : UNLOAD.comment;

    const hasComments = checkHasComments(loadComment, unloadComment);
    const cardHasError = hasError(testState, LOAD?.status, UNLOAD?.status);

    const handleCommentSave = (loadComment: string, unloadComment: string) => {
      if (LOAD.id) {
        installationValidationStore.updateComment(LOAD.id, loadComment);
      }
      if (UNLOAD.id) {
        installationValidationStore.updateComment(UNLOAD.id, unloadComment);
      }
      setIsModalOpen(false);
    };

    const showCommentsButton = shouldShowCommentButton(testState, LOAD.status, UNLOAD.status);

    return (
      <CardWrapper>
        <TestStatus $hasError={cardHasError}>
          <div className="card-header">
            <div className="card-header-content">
              <div className="card-header-title">
                <div className="card-title-content">
                  <Tooltip title={title}>
                    <div className="card-title">{title}</div>
                  </Tooltip>
                  <Tooltip title={subTitle}>
                    <div className="card-sub-title">{subTitle}</div>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          <Content>
            <div className="card-content-body">
              <div className="card-menu-item ">
                <div className={`card-menu-item-content`}>
                  <div className={`card-icon-wrapper `}>
                    <ConnectivityLoadIcon color={token.colorIcon} height={18} width={18} />
                  </div>
                  <div className="card-text-container ">
                    <div className="card-menu-text">{t('DEVICE_STATUS_LOAD')} </div>
                  </div>
                </div>
              </div>

              <div
                className={`test-data-wrapper test-result-common-wrapper ${
                  testState === TestState.INITIAL && 'hidden-visibility'
                } ${getClassNameByTestStateAndStatus(testState, LOAD.status, 'wrapper', token)}`}
              >
                {getStatusIcon(
                  testState,
                  LOAD.status,
                  getClassNameByTestStateAndStatus(testState, LOAD.status, 'color', token),
                )}
                <div
                  className={`test-content-wrapper ${getClassNameByTestStateAndStatus(
                    testState,
                    LOAD.status,
                    'content',
                    token,
                  )}  `}
                >
                  {getStatusText(LOAD.status, testState, LOAD.expectValue, LOAD.realValue)}
                </div>
              </div>

              <div className="card-menu-item">
                <div className={`card-menu-item-content`}>
                  <div className="card-icon-wrapper">
                    <ConnectivityUnloadIcon color={token.colorIcon} height={18} width={18} />
                  </div>
                  <div className="card-text-container">
                    <div className="card-menu-text">{t('DEVICE_STATUS_UNLOAD')} </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`test-data-wrapper margin-result-second-wrapper ${
                testState === TestState.INITIAL && 'hidden-visibility'
              } ${getClassNameByTestStateAndStatus(testState, UNLOAD.status, 'wrapper', token)}`}
            >
              {getStatusIcon(
                testState,
                UNLOAD.status,
                getClassNameByTestStateAndStatus(testState, UNLOAD.status, 'color', token),
              )}

              <div
                className={`test-content-wrapper ${getClassNameByTestStateAndStatus(
                  testState,
                  UNLOAD.status,
                  'content',
                  token,
                )} `}
              >
                {getStatusText(UNLOAD.status, testState, UNLOAD.expectValue, UNLOAD.realValue)}
              </div>
            </div>

            <Footer>
              {showCommentsButton && (
                <div className="card-comment-button" onClick={() => setIsModalOpen(true)}>
                  <div className="card-comment-icon-wrapper">
                    <MessageOutlinedIcon color={token.colorIcon} />
                  </div>
                  <div className="card-comment-button-text">{getCommentButtonText(testState, hasComments)}</div>
                </div>
              )}
              {isNoPowerSensorVisible && (
                <Tooltip title={t('NO_POWER_SENSOR')}>
                  <div className="card-no-power-icon-wrapper">
                    <LightningSlashIcon color={token.colorIcon} />
                  </div>
                </Tooltip>
              )}
            </Footer>
          </Content>
        </TestStatus>
        {isMobile ? (
          <MobileCommentDrawer
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleCommentSave}
            title={title}
            loadStatus={LOAD?.status}
            unloadStatus={UNLOAD?.status}
            initialLoadComment={loadComment ?? ''}
            initialUnloadComment={unloadComment ?? ''}
            isSubmitted={testState === TestState.SUBMITTED}
          />
        ) : (
          <CommentsModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleCommentSave}
            title={title}
            loadStatus={LOAD?.status}
            unloadStatus={UNLOAD?.status}
            initialLoadComment={loadComment ?? ''}
            initialUnloadComment={unloadComment ?? ''}
            isSubmitted={testState === TestState.SUBMITTED}
          />
        )}
      </CardWrapper>
    );
  },
);

export default TestStatusCard;
